import { RefObject, useEffect } from "react"

/**
 * Custom hook to detect clicks outside of specified elements and perform an action.
 *
 * @param {UseClickOutsideProps} props The properties for the hook.
 * @param {RefObject<HTMLElement>[]} props.refs Array of references to DOM elements to monitor.
 * @param {() => void} props.onOutsideClick The action to perform when a click occurs outside the monitored elements.
 *
 * This hook is useful for implementing UI behaviors such as closing a modal, dropdown, or context menu when the user
 * clicks outside of the component. It adds a mousedown event listener to the document and checks if the click target
 * is outside the specified elements by using their refs.
 *
 * Usage example:
 *
 * const modalRef = useRef(null);
 * const buttonRef = useRef(null);
 * useClickOutside({
 *   refs: [modalRef, buttonRef],
 *   onOutsideClick: () => {
 *     console.log('Clicked outside of the modal and button!');
 *   },
 * });
 */

type UseClickOutsideProps = {
    refs: RefObject<HTMLElement>[]
    onOutsideClick: () => void
}

export const useClickOutside = ({ refs, onOutsideClick }: UseClickOutsideProps) => {
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const isOutside = refs.every(ref => {
                return ref.current && !ref.current.contains(event.target as Node)
            })

            if (isOutside) {
                onOutsideClick()
            }
        }

        document.addEventListener("mousedown", handleClickOutside)

        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [refs, onOutsideClick])
}
